import React, { useState, useEffect } from 'react';
import apiClient from '@/utils/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Building2 } from 'lucide-react';

const GSTIN_RE = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;

const GstBilling = ({ studentId }) => {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [billing, setBilling] = useState(null); // saved state for label
  const [form, setForm] = useState({ enabled: false, billing_name: '', gstin: '', address: '', state: '' });

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await apiClient.get(`/finance/registration-status/${studentId}`);
        const gb = res.data?.gst_billing || { enabled: false };
        if (active) {
          setBilling(gb);
          setForm({ enabled: !!gb.enabled, billing_name: gb.billing_name || '', gstin: gb.gstin || '', address: gb.address || '', state: gb.state || '' });
        }
      } catch { /* non-fatal */ }
    })();
    return () => { active = false; };
  }, [studentId]);

  const save = async () => {
    if (form.enabled) {
      if (!form.billing_name.trim()) { toast.error('Enter the company/parent billing name'); return; }
      if (!GSTIN_RE.test((form.gstin || '').trim().toUpperCase())) { toast.error('Enter a valid 15-character GSTIN (e.g. 27ABCDE1234F1Z5)'); return; }
    }
    setSaving(true);
    try {
      const res = await apiClient.put(`/finance/students/${studentId}/gst-billing`, {
        enabled: form.enabled,
        billing_name: form.billing_name,
        gstin: (form.gstin || '').trim().toUpperCase(),
        address: form.address,
        state: form.state,
      });
      toast.success(form.enabled ? 'GST billing saved — used on all invoices for this student' : 'GST billing disabled');
      setBilling(res.data?.gst_billing);
      setOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save GST billing');
    } finally { setSaving(false); }
  };

  const gstinInvalid = form.gstin && !GSTIN_RE.test(form.gstin.trim().toUpperCase());

  return (
    <>
      <Button
        variant="outline"
        className={`w-full h-9 text-xs ${billing?.enabled ? 'border-blue-400 text-blue-700 bg-blue-50/50' : ''}`}
        data-testid="gst-billing-btn"
        onClick={() => setOpen(true)}
      >
        <Building2 className="w-3.5 h-3.5 mr-1.5" />
        {billing?.enabled ? `GST Billing: ${billing.gstin}` : 'Add GST / Company Billing (optional)'}
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-sm" data-testid="gst-billing-dialog">
          <DialogHeader>
            <DialogTitle>GST / Company Billing</DialogTitle>
            <DialogDescription>
              For GST-registered parents/companies. When enabled, these details print on the Proforma, Tax Invoice &amp; Receipt for this student.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Checkbox
                id="gst-enabled"
                checked={form.enabled}
                onCheckedChange={(v) => setForm((f) => ({ ...f, enabled: !!v }))}
                data-testid="gst-enabled-checkbox"
              />
              <Label htmlFor="gst-enabled" className="text-sm cursor-pointer">Bill to a GST-registered company / parent</Label>
            </div>
            {form.enabled && (
              <div className="space-y-2">
                <div>
                  <Label className="text-xs">Billing Name (Company / Parent)</Label>
                  <Input
                    value={form.billing_name}
                    onChange={(e) => setForm((f) => ({ ...f, billing_name: e.target.value }))}
                    placeholder="Acme Corp Pvt Ltd"
                    data-testid="gst-billing-name-input"
                  />
                </div>
                <div>
                  <Label className="text-xs">GSTIN</Label>
                  <Input
                    value={form.gstin}
                    onChange={(e) => setForm((f) => ({ ...f, gstin: e.target.value.toUpperCase() }))}
                    placeholder="27ABCDE1234F1Z5"
                    maxLength={15}
                    className={gstinInvalid ? 'border-red-400' : ''}
                    data-testid="gst-gstin-input"
                  />
                  {gstinInvalid && (
                    <p className="text-[11px] text-red-600 mt-0.5" data-testid="gst-gstin-warning">Invalid GSTIN format (must be 15 characters).</p>
                  )}
                </div>
                <div>
                  <Label className="text-xs">Billing Address</Label>
                  <Textarea
                    rows={2}
                    value={form.address}
                    onChange={(e) => setForm((f) => ({ ...f, address: e.target.value }))}
                    placeholder="Registered office address"
                    data-testid="gst-address-input"
                  />
                </div>
                <div>
                  <Label className="text-xs">State</Label>
                  <Input
                    value={form.state}
                    onChange={(e) => setForm((f) => ({ ...f, state: e.target.value }))}
                    placeholder="Maharashtra"
                    data-testid="gst-state-input"
                  />
                  <p className="text-[11px] text-gray-400 mt-0.5">Inter-state GSTINs are billed as IGST automatically.</p>
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} disabled={saving}>Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid="save-gst-billing-btn">
              {saving ? 'Saving...' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default GstBilling;
